import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { format } from 'date-fns'

const { FiX, FiSave } = FiIcons

const PlatoForm = ({ plato, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    fecha: format(new Date(), 'yyyy-MM-dd'),
    nombre: '',
    categoria: 'primero',
    descripcion: '',
    alergenos: [],
    precio: '',
    visible: true,
    orden: 0
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (plato) {
      setFormData({
        fecha: plato.fecha,
        nombre: plato.nombre,
        categoria: plato.categoria,
        descripcion: plato.descripcion || '',
        alergenos: plato.alergenos || [],
        precio: plato.precio ? plato.precio.toString() : '',
        visible: plato.visible,
        orden: plato.orden || 0
      })
    }
  }, [plato])

  const allergenOptions = [
    'gluten',
    'lacteos',
    'huevos',
    'frutos_secos',
    'pescado',
    'mariscos',
    'soja',
    'sulfitos'
  ]

  const categories = [
    { value: 'primero', label: 'Primero', requiresPrice: false },
    { value: 'segundo', label: 'Segundo', requiresPrice: false },
    { value: 'tipico', label: 'Típico Boliviano', requiresPrice: true },
    { value: 'parrilla', label: 'Parrilla', requiresPrice: true },
    { value: 'bebida_casa', label: 'Bebida Casa', requiresPrice: true },
    { value: 'bebida_botella', label: 'Bebida Botella', requiresPrice: true },
    { value: 'cerveza', label: 'Cerveza', requiresPrice: true }
  ]

  const selectedCategory = categories.find(cat => cat.value === formData.categoria)
  const requiresPrice = selectedCategory?.requiresPrice || false

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validation
    const newErrors = {}
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido'
    
    if (requiresPrice) {
      if (!formData.precio || isNaN(parseFloat(formData.precio))) {
        newErrors.precio = 'El precio es requerido para esta categoría'
      }
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0) {
      const platoData = {
        ...formData,
        precio: requiresPrice ? parseFloat(formData.precio) : null
      }
      onSave(platoData)
    }
  }

  const handleAlergenToggle = (alergeno) => {
    setFormData(prev => ({
      ...prev,
      alergenos: prev.alergenos.includes(alergeno)
        ? prev.alergenos.filter(a => a !== alergeno)
        : [...prev.alergenos, alergeno]
    }))
  }

  const handleCategoryChange = (categoria) => {
    setFormData(prev => ({
      ...prev,
      categoria,
      precio: categories.find(cat => cat.value === categoria)?.requiresPrice ? prev.precio : ''
    }))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-vip-orange">
              {plato ? 'Editar Plato' : 'Nuevo Plato'}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-vip-brown mb-2">
                  Fecha *
                </label>
                <input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vip-brown mb-2">
                  Categoría *
                </label>
                <select
                  value={formData.categoria}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
                  required
                >
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {!requiresPrice && (
                  <p className="mt-1 text-xs text-blue-600">
                    Esta categoría forma parte del menú del día (13€)
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-vip-brown mb-2">
                Nombre del Plato *
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange ${
                  errors.nombre ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ej: Jamón Ibérico con Pan Tumaca"
                required
              />
              {errors.nombre && (
                <p className="mt-1 text-sm text-red-600">{errors.nombre}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-vip-brown mb-2">
                Descripción
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
                placeholder="Descripción detallada del plato..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-vip-brown mb-3">
                Alérgenos
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {allergenOptions.map((alergeno) => (
                  <label
                    key={alergeno}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.alergenos.includes(alergeno)}
                      onChange={() => handleAlergenToggle(alergeno)}
                      className="rounded border-gray-300 text-vip-orange focus:ring-vip-orange"
                    />
                    <span className="text-sm text-gray-700 capitalize">
                      {alergeno.replace('_', ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {requiresPrice && (
                <div>
                  <label className="block text-sm font-medium text-vip-brown mb-2">
                    Precio (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.precio}
                    onChange={(e) => setFormData(prev => ({ ...prev, precio: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange ${
                      errors.precio ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                    required
                  />
                  {errors.precio && (
                    <p className="mt-1 text-sm text-red-600">{errors.precio}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-vip-brown mb-2">
                  Orden
                </label>
                <input
                  type="number"
                  value={formData.orden}
                  onChange={(e) => setFormData(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
                  placeholder="0"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.visible}
                    onChange={(e) => setFormData(prev => ({ ...prev, visible: e.target.checked }))}
                    className="rounded border-gray-300 text-vip-orange focus:ring-vip-orange"
                  />
                  <span className="text-sm font-medium text-vip-brown">
                    Visible en el menú
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex items-center px-4 py-2 bg-vip-orange text-white rounded-md hover:bg-vip-dark transition-colors duration-200"
              >
                <SafeIcon icon={FiSave} className="mr-2" />
                {plato ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  )
}

export default PlatoForm